import React, { useEffect, useRef } from 'react';

const GradientBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Устанавливаем размер canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Функция создания градиента
    const createGradient = (time: number) => {
      const width = canvas.width;
      const height = canvas.height;

      // Создаем радиальный градиент
      const gradient = ctx.createRadialGradient(
        width / 2 + Math.sin(time * 0.001) * 100,
        height / 2 + Math.cos(time * 0.001) * 100,
        0,
        width / 2,
        height / 2,
        Math.max(width, height) * 0.8
      );

      // Динамические цвета - более спокойные
      const hue1 = (time * 0.02) % 360; // Замедлил в 5 раз
      const hue2 = (hue1 + 30) % 360; // Уменьшил разброс
      const hue3 = (hue2 + 30) % 360;

      gradient.addColorStop(0, `hsla(${hue1}, 60%, 85%, 0.2)`); // Больше насыщенности
      gradient.addColorStop(0.5, `hsla(${hue2}, 60%, 85%, 0.16)`);
      gradient.addColorStop(1, `hsla(${hue3}, 60%, 85%, 0.12)`);

      return gradient;
    };

    // Функция анимации
    const animate = () => {
      timeRef.current += 16; // ~60fps
      
      // Отладочная информация убрана

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Рисуем основной градиент
      const gradient = createGradient(timeRef.current);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Добавляем дополнительные градиентные круги - более спокойные
      const circles = 4; // Увеличил количество для более заметного эффекта
      for (let i = 0; i < circles; i++) {
        const angle = (timeRef.current * 0.0005 + (i * Math.PI * 2) / circles) % (Math.PI * 2); // Замедлил в 4 раза
        const x = canvas.width / 2 + Math.cos(angle) * 150; // Уменьшил радиус движения
        const y = canvas.height / 2 + Math.sin(angle) * 150;
        const radius = 100 + Math.sin(timeRef.current * 0.001 + i) * 30; // Уменьшил амплитуду

        const circleGradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        const hue = (timeRef.current * 0.01 + i * 60) % 360; // Замедлил и уменьшил разброс
        
        circleGradient.addColorStop(0, `hsla(${hue}, 55%, 85%, 0.18)`); // Больше насыщенности
        circleGradient.addColorStop(1, `hsla(${hue}, 55%, 85%, 0)`);

        ctx.fillStyle = circleGradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ background: 'transparent', zIndex: 0 }}
    />
  );
};

export default GradientBackground; 